/**
 * Web Push Notification helpers.
 * Requests permission and shows native notifications for incoming messages.
 * Uses Service Worker for background notifications.
 */

/** Request notification permission from the user */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.log('[Notifications] Not supported in this browser');
    return false;
  }

  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const result = await Notification.requestPermission();
  return result === 'granted';
}

/** Check if notifications are permitted */
export function isNotificationPermitted(): boolean {
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

/** Show a notification - prefers Service Worker for background support */
export function showNotification(title: string, body: string, options?: { tag?: string; onClick?: () => void }) {
  if (!isNotificationPermitted()) return;

  const tag = options?.tag || 'logi-message';

  // Always try Service Worker first (works in background)
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then(reg => {
      // Send message to SW to show notification
      if (reg.active) {
        reg.active.postMessage({
          type: 'SHOW_NOTIFICATION',
          title,
          body,
          tag,
          url: window.location.origin,
        });
      } else {
        // Fallback: use showNotification directly on registration
        reg.showNotification(title, {
          body,
          icon: '/pwa-192x192-v8.png',
          badge: '/pwa-192x192-v8.png',
          tag,
          renotify: true,
        } as any);
      }
    }).catch(() => {
      // Final fallback: native Notification API
      showNativeNotification(title, body, tag, options?.onClick);
    });
  } else {
    showNativeNotification(title, body, tag, options?.onClick);
  }
}

/** Fallback native notification */
function showNativeNotification(title: string, body: string, tag: string, onClick?: () => void) {
  try {
    const notification = new Notification(title, {
      body,
      icon: '/pwa-192x192-v8.png',
      badge: '/pwa-192x192-v8.png',
      tag,
      silent: false,
    });

    if (onClick) {
      notification.onclick = () => {
        window.focus();
        onClick();
        notification.close();
      };
    }

    setTimeout(() => notification.close(), 5000);
  } catch {
    // Not available
  }
}
